import React, { useCallback, useEffect, useState } from "react";
import type {
  Alert,
  Asset,
  CheckSummary,
  Incident,
  Page,
} from "@ghostwatch/shared-types";
import { AsyncState, Button, Cell, Panel, Row, StatusPill, Table, formatTime } from "./components";
import { errorMessage, useSession } from "./session";

/** Loads a page of records, cancelling in flight when the view unmounts. */
function useList<T>(load: (signal: AbortSignal) => Promise<Page<T>>, deps: unknown[]) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    load(ac.signal)
      .then((page) => setItems(page.items))
      .catch((e) => {
        if (ac.signal.aborted) return;
        setError(errorMessage(e));
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(refresh, [refresh]);
  return { items, loading, error, refresh };
}

export function AssetsPage() {
  const { client } = useSession();
  const { items, loading, error } = useList<Asset>(
    (signal) => client.listAssets({ limit: 100, signal }),
    [client],
  );

  return (
    <Panel title="Assets">
      <AsyncState loading={loading} error={error} empty={items.length === 0} emptyText="No assets yet.">
        <Table head={["Name", "Type", "Environment", "Status", "Last healthy"]}>
          {items.map((a) => (
            <Row key={a.id}>
              <Cell>{a.display_name}</Cell>
              <Cell mono>{a.asset_type}</Cell>
              <Cell>{a.environment}</Cell>
              <Cell>
                <StatusPill domain="asset" value={a.status} />
              </Cell>
              <Cell mono>{formatTime(a.last_healthy_at)}</Cell>
            </Row>
          ))}
        </Table>
      </AsyncState>
    </Panel>
  );
}

export function ChecksPage() {
  const { client } = useSession();
  const { items, loading, error, refresh } = useList<CheckSummary>(
    (signal) => client.listChecks({ limit: 100, signal }),
    [client],
  );
  const [busy, setBusy] = useState<string | null>(null);

  async function runNow(id: string) {
    setBusy(id);
    try {
      await client.runCheck(id);
      refresh();
    } catch {
      // The row keeps its previous state; the next refresh reconciles.
    } finally {
      setBusy(null);
    }
  }

  return (
    <Panel title="Checks">
      <AsyncState loading={loading} error={error} empty={items.length === 0} emptyText="No checks configured.">
        <Table head={["Name", "Type", "Status", "Last run", ""]}>
          {items.map((c) => (
            <Row key={c.id}>
              <Cell>{c.name}</Cell>
              <Cell mono>{c.check_type}</Cell>
              <Cell>
                <StatusPill domain="check" value={c.status} />
              </Cell>
              <Cell mono>{formatTime(c.last_run_at)}</Cell>
              <Cell>
                <Button onClick={() => runNow(c.id)} disabled={busy === c.id}>
                  {busy === c.id ? "Queueing…" : "Run now"}
                </Button>
              </Cell>
            </Row>
          ))}
        </Table>
      </AsyncState>
    </Panel>
  );
}

export function AlertsPage() {
  const { client } = useSession();
  const { items, loading, error, refresh } = useList<Alert>(
    (signal) => client.listAlerts({ limit: 100, signal }),
    [client],
  );
  const [busy, setBusy] = useState<string | null>(null);

  async function acknowledge(id: string) {
    setBusy(id);
    try {
      await client.ackAlert(id);
      refresh();
    } finally {
      setBusy(null);
    }
  }

  return (
    <Panel title="Alerts">
      <AsyncState loading={loading} error={error} empty={items.length === 0} emptyText="No alerts.">
        <Table head={["Summary", "Severity", "State", "Opened", ""]}>
          {items.map((a) => (
            <Row key={a.id}>
              <Cell>
                {a.summary}
                {/* A suppressed alert still exists and still matters — say so rather
                    than hiding the row, which would read as "nothing happened". */}
                {a.suppressed_by_window && (
                  <span className="ml-2 text-xs text-[var(--tone-suppressed)]">
                    (suppressed by maintenance window)
                  </span>
                )}
              </Cell>
              <Cell>
                <StatusPill domain="alert" value={a.severity} />
              </Cell>
              <Cell mono>{a.state}</Cell>
              <Cell mono>{formatTime(a.opened_at)}</Cell>
              <Cell>
                {a.acknowledged_at ? (
                  <span className="text-xs text-[var(--text-dim)]">Acked</span>
                ) : (
                  <Button onClick={() => acknowledge(a.id)} disabled={busy === a.id}>
                    {busy === a.id ? "…" : "Acknowledge"}
                  </Button>
                )}
              </Cell>
            </Row>
          ))}
        </Table>
      </AsyncState>
    </Panel>
  );
}

export function IncidentsPage() {
  const { client } = useSession();
  const { items, loading, error } = useList<Incident>(
    (signal) => client.listIncidents({ limit: 100, signal }),
    [client],
  );

  return (
    <Panel title="Incidents">
      <AsyncState loading={loading} error={error} empty={items.length === 0} emptyText="No incidents.">
        <Table head={["Title", "Severity", "Status", "Opened", "Resolved"]}>
          {items.map((i) => (
            <Row key={i.id}>
              <Cell>{i.title}</Cell>
              <Cell>
                <StatusPill domain="alert" value={i.severity} />
              </Cell>
              <Cell>
                <StatusPill domain="incident" value={i.status} />
              </Cell>
              <Cell mono>{formatTime(i.opened_at)}</Cell>
              <Cell mono>{formatTime(i.resolved_at)}</Cell>
            </Row>
          ))}
        </Table>
      </AsyncState>
    </Panel>
  );
}

export function LoginPage() {
  const { login } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-full max-w-sm flex-col justify-center px-6">
      <h1 className="mb-1 text-lg font-semibold">GhostWatch</h1>
      <p className="mb-6 text-sm text-[var(--text-dim)]">Sign in to the operator console.</p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block text-[var(--text-dim)]">Email</span>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded border border-[var(--border)] bg-[var(--panel)] px-2.5 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-[var(--text-dim)]">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded border border-[var(--border)] bg-[var(--panel)] px-2.5 py-1.5 text-sm"
          />
        </label>
        {error && (
          <p role="alert" className="rounded border border-[var(--tone-critical-border)] bg-[var(--tone-critical-bg)] px-3 py-2 text-sm text-[var(--tone-critical)]">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </main>
  );
}
